// The Personas list + editor Drawer (personas.md; List archetype §4c).
// Renders scoped to one agent when the page carries `?agent=`, else the
// working team's list (`team_id`, docs/09 §4). Container owns the queries +
// mutations;
// `PersonasListView` is props-only for tests/stories. Rows open the edit Drawer;
// its delete action keeps the §2.12 inline confirm beside any InlineError
// (Toast is never the sole surface for a blocking error, §4a). Edit saves split
// per field owner: a changed spec goes
// through `:revise` (mints a revision), a changed display name through PATCH
// (metadata, no revision); unchanged saves call neither.
import { useMemo, useState, type ReactNode } from "react";

import {
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../../lib/errors";
import { listPhoneNumbers } from "../../lib/phoneNumbers";
import { queryKeys } from "../../lib/queryKeys";
import { useResolvedWorkingTeamId } from "../../lib/workingScope";
import { shortResourceId } from "../../lib/shellNavigation";
import {
  useListQuery,
  type ListQueryPage,
  type ListQueryResult,
} from "../../lib/useListQuery";
import {
  Button,
  Drawer,
  EmptyState,
  Inline,
  InlineError,
  PageHeader,
  Skeleton,
  Stack,
  Text,
  useToast,
} from "../../ui";
import { PaginatedTableSection } from "../shared/PaginatedTableSection";
import { DateCell } from "../shared/DateCell";
import { NameWithId } from "../shared/NameWithId";
import { RowConfirm } from "../shared/RowConfirm";
import { PersonaEditor, type PersonaAgentOption } from "./PersonaEditor";
import { PersonaHistory } from "./PersonaHistory";
import {
  personaLlmLabel,
  personaNumberLabel,
  personaPhoneEndpointLabel,
  personaVoiceLabel,
  personaVoiceProviderLabel,
} from "./personaLabels";
import {
  personaSpecEquals,
  personaSpecForSave,
  unavailablePersonaProviders,
  type PersonaFormSubmission,
} from "./personaSpec";
import { personaSupplyCount } from "./PersonaVariablesSection";
import type { VariableResponse } from "../../lib/variableCascade";
import { useVariableDeclarations } from "../../lib/useVariableDeclarations";

type PersonaCreate = components["schemas"]["PersonaCreate"];
type PersonaProviderPolicy =
  components["schemas"]["PersonaProviderPolicyResponse"];
type PersonaResponse = components["schemas"]["PersonaResponse"];
type PersonaRevise = components["schemas"]["PersonaRevise"];
type PhoneNumberResponse = components["schemas"]["PhoneNumberResponse"];

// What the drawer shows: create form, resolved edit subject, deep-linked
// subject still loading, or the not-found / cross-agent error state.
export type PersonaDrawerState =
  | { mode: "create" }
  | { mode: "edit"; persona: PersonaResponse }
  | { mode: "loading" }
  | { message: string; mode: "error" };

export interface PersonasListProps {
  // Present = the page carries an agent scope; absent = all personas.
  agentId?: string;
  // The PageHeader's create action, owned by the route container (it owns the
  // `?new=persona` URL state).
  createAction: ReactNode;
  editingPersonaId: string | null;
  isCreateOpen: boolean;
  onCloseEditor: () => void;
  onOpenEdit: (personaId: string) => void;
}

export function PersonasList({
  agentId,
  createAction,
  editingPersonaId,
  isCreateOpen,
  onCloseEditor,
  onOpenEdit,
}: PersonasListProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [deletePersonaId, setDeletePersonaId] = useState<string | null>(null);

  // All agents = the working team's personas (docs/09 §4); held until the
  // shell resolves the team. Agent wins: never both filters.
  const workingTeamId = useResolvedWorkingTeamId();
  const personasListKey =
    agentId != null
      ? queryKeys.personas.list({ agent_id: agentId })
      : queryKeys.personas.list({ team_id: workingTeamId });
  const personas = useListQuery("/v1/personas", {
    enabled: agentId != null || workingTeamId !== undefined,
    params:
      agentId != null ? { agent_id: agentId } : { team_id: workingTeamId },
    queryKey: personasListKey,
  });

  // The deployment's enabled persona providers (#1441): drives the editor's
  // provider/voice pickers and the list's provider labels. Fail-closed while
  // /v1/me is pending — nothing is offered that is not server-confirmed.
  const me = useQuery({
    queryKey: queryKeys.me,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/me");
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });
  const providerPolicy =
    me.data?.persona_provider_policy ?? unavailablePersonaProviders;

  // ASSUMED(no count in the All-agents view; it would cost one declarations
  // load per agent).
  const declarations = useVariableDeclarations(agentId ?? "");

  // Unscoped views resolve agent display names for the Agent column and the
  // create form's owning-agent Select (first pages via the shared keyset
  // list, same budget as the shell's agent selector).
  const agents = useListQuery("/v1/agents", {
    enabled: agentId === undefined,
    queryKey: queryKeys.agents.list({ surface: "personas" }),
  });
  const agentNamesById = useMemo(
    () => new Map(agents.items.map((agent) => [agent.id, agent.display_name])),
    [agents.items],
  );

  // The org's synced telephony inventory: the editor's pinned-number picker
  // and the list's Number column both read it, so a saved `pool_number_ref`
  // renders as its E.164 address instead of a bare row id. `listPhoneNumbers`
  // walks every keyset page — the same complete list the settings screen
  // splits — so a pinnable number is never hidden behind an unfetched page.
  const phoneNumbers = useQuery({
    queryKey: queryKeys.phoneNumbers.list(),
    queryFn: listPhoneNumbers,
  });
  const phoneNumberItems = useMemo(
    () => phoneNumbers.data ?? [],
    [phoneNumbers.data],
  );
  const phoneNumberAddressesById = useMemo(
    () =>
      new Map(phoneNumberItems.map((number) => [number.id, number.address])),
    [phoneNumberItems],
  );

  // Drawer edit subject: detail query seeded from the list cache so an
  // in-page Edit opens instantly; a deep link resolves over the network.
  const personaDetail = useQuery({
    enabled: editingPersonaId != null,
    queryKey: queryKeys.personas.detail(editingPersonaId ?? "pending"),
    queryFn: async () => {
      if (editingPersonaId == null) {
        throw new Error("Persona id is required.");
      }
      const { data, error } = await apiClient.GET("/v1/personas/{id}", {
        params: { path: { id: editingPersonaId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    initialData: () =>
      queryClient
        .getQueryData<InfiniteData<ListQueryPage<"/v1/personas">>>(
          personasListKey,
        )
        ?.pages.flatMap((page) => page.items)
        .find((persona) => persona.id === editingPersonaId),
  });

  // A drawer subject never crosses an ACTIVE agent scope: a resolved subject
  // whose agent_id mismatches the scope is treated as not-found. Unscoped
  // pages accept any persona in the org.
  const drawer: PersonaDrawerState | null = isCreateOpen
    ? { mode: "create" }
    : editingPersonaId == null
      ? null
      : personaDetail.data !== undefined
        ? agentId === undefined || personaDetail.data.agent_id === agentId
          ? { mode: "edit", persona: personaDetail.data }
          : { message: "Persona not found for this agent.", mode: "error" }
        : personaDetail.isError
          ? {
              message: requestErrorDescription(personaDetail.error),
              mode: "error",
            }
          : { mode: "loading" };

  const savePersona = useMutation({
    mutationFn: async ({
      persona,
      submission,
    }: {
      persona: PersonaResponse | null;
      submission: PersonaFormSubmission;
    }) => {
      if (persona) {
        let latest = persona;
        const spec = personaSpecForSave(persona, submission);
        if (!personaSpecEquals(spec, persona.current_revision.spec)) {
          const body = { spec } satisfies PersonaRevise;
          const { data, error } = await apiClient.POST(
            "/v1/personas/{id}:revise",
            {
              body,
              params: { path: { id: persona.id } },
            },
          );
          if (error) {
            throw new ApiRequestError(error);
          }
          latest = data;
        }
        if (submission.displayName !== persona.display_name) {
          const { data, error } = await apiClient.PATCH("/v1/personas/{id}", {
            body: { display_name: submission.displayName },
            params: { path: { id: persona.id } },
          });
          if (error) {
            throw new ApiRequestError(error);
          }
          latest = data;
        }
        return latest;
      }

      const body = {
        agent_id: submission.agentId,
        display_name: submission.displayName,
        spec: personaSpecForSave(null, submission),
      } satisfies PersonaCreate;
      const { data, error } = await apiClient.POST("/v1/personas", { body });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (persona) => {
      setDeletePersonaId(null);
      onCloseEditor();
      queryClient.setQueryData(queryKeys.personas.detail(persona.id), persona);
      toast.success("Persona saved");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.personas.all,
      });
    },
    onError: (error) => {
      toast.error("Persona save failed", {
        description: requestErrorDescription(error),
      });
    },
  });

  const deletePersona = useMutation({
    mutationFn: async (persona: PersonaResponse) => {
      const { error } = await apiClient.DELETE("/v1/personas/{id}", {
        params: { path: { id: persona.id } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return persona;
    },
    onSuccess: async () => {
      setDeletePersonaId(null);
      onCloseEditor();
      toast.success("Persona deleted");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.personas.all,
      });
    },
    onError: (error) => {
      // The inline confirm stays open (deletePersonaId untouched) so the
      // operator can retry; the row renders the InlineError.
      toast.error("Persona delete failed", {
        description: requestErrorDescription(error),
      });
    },
  });

  const agentOptions: PersonaAgentOption[] | undefined =
    agentId === undefined
      ? agents.items.map((agent) => ({
          id: agent.id,
          label: agent.display_name,
        }))
      : undefined;

  return (
    <PersonasListView
      agentNamesById={agentNamesById}
      agentOptions={agentOptions}
      declarations={declarations.data}
      declarationsState={
        declarations.isPending
          ? "loading"
          : declarations.isError
            ? "error"
            : "ready"
      }
      createAction={createAction}
      historySlot={
        drawer?.mode === "edit" ? (
          <PersonaHistory personaId={drawer.persona.id} />
        ) : null
      }
      deleteError={
        deletePersona.isError
          ? requestErrorDescription(deletePersona.error)
          : null
      }
      deletePersonaId={deletePersonaId}
      drawer={drawer}
      isDeletePending={deletePersona.isPending}
      isSavePending={savePersona.isPending}
      scopedAgentId={agentId}
      onCancelDelete={() => {
        deletePersona.reset();
        setDeletePersonaId(null);
      }}
      onCloseEditor={() => {
        deletePersona.reset();
        setDeletePersonaId(null);
        onCloseEditor();
      }}
      onConfirmDelete={(persona) => {
        deletePersona.mutate(persona);
      }}
      onOpenEdit={onOpenEdit}
      onSave={async (submission) => {
        await savePersona.mutateAsync({
          persona: drawer?.mode === "edit" ? drawer.persona : null,
          submission,
        });
      }}
      onStartDelete={(persona) => {
        deletePersona.reset();
        setDeletePersonaId(persona.id);
      }}
      personas={personas}
      canBuyNumbers={me.data?.number_provisioning_enabled === true}
      phoneNumbers={phoneNumberItems}
      phoneNumberAddressesById={phoneNumberAddressesById}
      phoneNumbersError={
        phoneNumbers.isError
          ? requestErrorDescription(phoneNumbers.error)
          : null
      }
      isPhoneNumbersLoading={phoneNumbers.isPending}
      providerPolicy={providerPolicy}
      saveError={
        savePersona.isError ? requestErrorDescription(savePersona.error) : null
      }
    />
  );
}

export interface PersonasListViewProps {
  agentNamesById?: ReadonlyMap<string, string>;
  agentOptions?: PersonaAgentOption[];
  createAction: ReactNode;
  declarations?: readonly VariableResponse[];
  declarationsState?: "error" | "loading" | "ready";
  deleteError: string | null;
  deletePersonaId: string | null;
  drawer: PersonaDrawerState | null;
  // The PersonaHistory block for the edit drawer, injected by the container
  // so the view stays props-pure (query-free) for tests/stories.
  historySlot?: ReactNode;
  isDeletePending: boolean;
  isPhoneNumbersLoading: boolean;
  isSavePending: boolean;
  onCancelDelete: () => void;
  onCloseEditor: () => void;
  onConfirmDelete: (persona: PersonaResponse) => void;
  onOpenEdit: (personaId: string) => void;
  onSave: (submission: PersonaFormSubmission) => Promise<void>;
  onStartDelete: (persona: PersonaResponse) => void;
  personas: ListQueryResult<PersonaResponse>;
  // The org's synced telephony inventory (GET /v1/phone-numbers): the
  // editor's picker options, plus the ref -> E.164 map the Number column
  // renders. Empty while pending or when nothing is synced.
  canBuyNumbers?: boolean;
  phoneNumbers: readonly PhoneNumberResponse[];
  phoneNumberAddressesById: ReadonlyMap<string, string>;
  phoneNumbersError: string | null;
  // Enabled persona providers from GET /v1/me (#1441); the container passes
  // the fail-closed empty policy while the read is pending.
  providerPolicy: PersonaProviderPolicy;
  saveError: string | null;
  scopedAgentId?: string;
}

export function PersonasListView({
  agentNamesById,
  agentOptions,
  createAction,
  declarations,
  declarationsState = "ready",
  deleteError,
  deletePersonaId,
  drawer,
  historySlot,
  isDeletePending,
  isPhoneNumbersLoading,
  isSavePending,
  onCancelDelete,
  onCloseEditor,
  onConfirmDelete,
  onOpenEdit,
  onSave,
  onStartDelete,
  personas,
  canBuyNumbers = false,
  phoneNumbers,
  phoneNumberAddressesById,
  phoneNumbersError,
  providerPolicy,
  saveError,
  scopedAgentId,
}: PersonasListViewProps) {
  const showAgentColumn = scopedAgentId === undefined;
  const columns = useMemo(
    () =>
      personaColumns({
        agentNamesById,
        phoneNumberAddressesById,
        providerPolicy,
        declarations: declarations ?? [],
        declarationsState,
        showAgentColumn,
      }),
    [
      agentNamesById,
      declarations,
      declarationsState,
      phoneNumberAddressesById,
      providerPolicy,
      showAgentColumn,
    ],
  );

  return (
    <>
      <PageHeader compact actions={createAction} />
      <PaginatedTableSection
        ariaLabel="Personas"
        columns={columns}
        emptyState={<EmptyState title="No personas" />}
        enableSorting
        errorMessage={personas.errorMessage}
        getRowId={(persona) => persona.id}
        hasNextPage={personas.hasNextPage}
        isFetchingNextPage={personas.isFetchingNextPage}
        isPending={personas.isPending}
        items={personas.items}
        listStateKey={personas.listStateKey}
        onLoadMore={personas.fetchNextPage}
        onRowClick={(row) => {
          onOpenEdit(row.original.id);
        }}
        skeletonLabel="Loading personas"
        skeletonRows={5}
        virtual={{ fill: true, interactiveRows: true }}
      />
      {drawer ? (
        <Drawer
          ariaLabel={
            drawer.mode === "create" ? "Create persona" : "Edit persona"
          }
          isOpen
          onDismiss={onCloseEditor}
        >
          {drawer.mode === "loading" ? (
            <Skeleton label="Loading persona" rows={5} shape="detail-field" />
          ) : drawer.mode === "error" ? (
            <Stack gap={4}>
              <Text variant="caps">Edit persona</Text>
              <InlineError>{drawer.message}</InlineError>
              <Inline>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onCloseEditor}
                >
                  Close
                </Button>
              </Inline>
            </Stack>
          ) : (
            <Stack gap={6}>
              <PersonaEditor
                agentOptions={
                  drawer.mode === "create" ? agentOptions : undefined
                }
                error={saveError}
                isPending={isSavePending}
                isPhoneNumbersLoading={isPhoneNumbersLoading}
                canBuyNumbers={canBuyNumbers}
                phoneNumbers={phoneNumbers}
                phoneNumbersError={phoneNumbersError}
                providerPolicy={providerPolicy}
                scopedAgentId={scopedAgentId}
                onCancel={onCloseEditor}
                onSubmit={onSave}
                persona={drawer.mode === "edit" ? drawer.persona : null}
              />
              {drawer.mode === "edit" ? historySlot : null}
              {drawer.mode === "edit" ? (
                <PersonaDeleteAction
                  deleteError={deleteError}
                  isConfirmingDelete={deletePersonaId === drawer.persona.id}
                  isDeletePending={isDeletePending}
                  persona={drawer.persona}
                  onCancelDelete={onCancelDelete}
                  onConfirmDelete={onConfirmDelete}
                  onStartDelete={onStartDelete}
                />
              ) : null}
            </Stack>
          )}
        </Drawer>
      ) : null}
    </>
  );
}

interface PersonaColumnHandlers {
  agentNamesById?: ReadonlyMap<string, string>;
  declarations: readonly VariableResponse[];
  declarationsState: "error" | "loading" | "ready";
  phoneNumberAddressesById: ReadonlyMap<string, string>;
  providerPolicy: PersonaProviderPolicy;
  showAgentColumn: boolean;
}

function personaColumns(
  handlers: PersonaColumnHandlers,
): Array<ColumnDef<PersonaResponse>> {
  const agentName = (persona: PersonaResponse) =>
    handlers.agentNamesById?.get(persona.agent_id) ??
    shortResourceId(persona.agent_id);

  const columns: Array<ColumnDef<PersonaResponse>> = [
    {
      header: "Name",
      id: "name",
      accessorFn: (persona) => persona.display_name,
      cell: ({ row }) => (
        <NameWithId
          id={row.original.id}
          idLabel="Persona ID"
          name={row.original.display_name}
          strong
        />
      ),
    },
  ];

  if (handlers.showAgentColumn) {
    columns.push({
      header: "Agent",
      id: "agent",
      accessorFn: (persona) => agentName(persona),
      cell: ({ row }) => (
        <NameWithId
          id={row.original.agent_id}
          idLabel="Agent ID"
          name={agentName(row.original)}
        />
      ),
    });
  }

  if (!handlers.showAgentColumn) {
    columns.push({
      header: "Supplies",
      id: "supplies",
      accessorFn: (persona) =>
        handlers.declarationsState === "ready"
          ? personaSupplyCount(
              handlers.declarations,
              persona.id,
              persona.current_revision.spec,
            )
          : null,
      cell: ({ row }) =>
        handlers.declarationsState === "loading"
          ? "Loading variables..."
          : handlers.declarationsState === "error"
            ? "Variables are unavailable."
            : personaSupplyCount(
                handlers.declarations,
                row.original.id,
                row.original.current_revision.spec,
              ),
    });
  }

  columns.push(
    {
      header: "Number selection",
      id: "number",
      accessorFn: personaNumberLabel,
      cell: ({ row }) =>
        personaPhoneEndpointLabel(
          row.original,
          handlers.phoneNumberAddressesById,
        ),
    },
    {
      header: "Voice",
      id: "voice",
      accessorFn: (persona) =>
        personaVoiceProviderLabel(persona, handlers.providerPolicy),
      cell: ({ row }) =>
        personaVoiceLabel(row.original, handlers.providerPolicy),
    },
    {
      header: "LLM",
      id: "llm",
      accessorFn: (persona) =>
        personaLlmLabel(persona, handlers.providerPolicy),
      cell: ({ row }) => personaLlmLabel(row.original, handlers.providerPolicy),
    },
    {
      header: "Created",
      id: "created",
      accessorFn: (persona) => persona.created_at,
      cell: ({ row }) => <DateCell value={row.original.created_at} />,
    },
    {
      header: "Updated",
      id: "updated",
      accessorFn: (persona) => persona.updated_at,
      cell: ({ row }) => <DateCell value={row.original.updated_at} />,
    },
  );

  return columns;
}

interface PersonaDeleteActionProps {
  deleteError: string | null;
  isConfirmingDelete: boolean;
  isDeletePending: boolean;
  onCancelDelete: () => void;
  onConfirmDelete: (persona: PersonaResponse) => void;
  onStartDelete: (persona: PersonaResponse) => void;
  persona: PersonaResponse;
}

function PersonaDeleteAction({
  deleteError,
  isConfirmingDelete,
  isDeletePending,
  onCancelDelete,
  onConfirmDelete,
  onStartDelete,
  persona,
}: PersonaDeleteActionProps) {
  return (
    <Stack gap={2}>
      <RowConfirm
        confirmLabel="Confirm delete"
        isConfirming={isConfirmingDelete}
        isPending={isConfirmingDelete && isDeletePending}
        isTriggerDisabled={isDeletePending}
        onCancel={onCancelDelete}
        onConfirm={() => {
          onConfirmDelete(persona);
        }}
        onStart={() => {
          onStartDelete(persona);
        }}
        pendingLabel="Deleting..."
        sentence={`Delete ${persona.display_name}? This removes the persona from its agent.`}
        triggerLabel="Delete persona"
      />
      {isConfirmingDelete && deleteError ? (
        <InlineError>{deleteError}</InlineError>
      ) : null}
    </Stack>
  );
}
